// "Recordings" on /shares: what I recorded and what I was invited to review
// (RFC-043 "Recordings"), as rows (RecordingRow), the two under their own
// headings when there are both — To review first, then Mine. It loads on its
// own, so a relay without recordings, or a failure here, leaves the shares
// beside it alone, and says how many it has to the tab it sits under. Its
// owner deletes a recording after the page asks (byj-1wv.33), several at once
// (byj-1wv.43), and edits its name and how long it is kept (byj-1wv.38,
// byj-1wv.43), from its ⋯ menu. A class's recordings are a list of their own
// (`scope` "class", byj-1wv.51.6): the person's own made in a class, kept for
// it, its instructor reading those of an assignment, so they stand apart from
// the rest — each class under its name, with the way to it.
import { useEffect, useMemo, useState } from "react";
import { api, type Recording, type RecordingPatch, type ReviewRequestResult } from "../api";
import { t, phrases } from "../i18n";
import { useConfirm } from "./Confirm";
import { useEditRecording } from "./EditRecording";
import { SelectControls, useSelectMode } from "./Selection";
import { useRequestReview } from "./RequestReview";
import { FilterChips, inForce, useFilter, type Chip } from "./FilterChips";
import { Link } from "./Nav";
import { Grouped, groupsBy, type Group } from "./Rows";
import { instructorLoses, RecordingRow, reviewKnown } from "./RecordingRow";
import { sectionOf } from "../kinds";

export function RecordingList({
  load = api.recordings,
  remove = api.deleteRecording,
  update = api.updateRecording,
  requestReviews = api.requestReviews,
  onCount,
  scope = "own",
}: {
  load?: () => Promise<Recording[]>;
  remove?: (id: string) => Promise<void>;
  update?: (id: string, patch: RecordingPatch) => Promise<Recording>;
  requestReviews?: (recordings: string[], emails: string[], note: string) => Promise<{ results: ReviewRequestResult[] }>;
  /** How many there are, and how many of them others asked me to review, once loaded and after each delete: the tab's counts. */
  onCount?: (n: number, toReview: number) => void;
  /** The person's own recordings and those they review ("own"), or those made in a class ("class"). */
  scope?: "own" | "class";
}) {
  const [recs, setRecs] = useState<Recording[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, confirmDialog] = useConfirm();
  const [editRecording, editDialog] = useEditRecording();
  const mode = useSelectMode();
  const [askReview, reviewDialog] = useRequestReview();
  const [sentNote, setSentNote] = useState<string | null>(null);
  const classes = scope === "class";
  // a class's are the student's own made in one; one they were asked to
  // review stays under To review with the rest, its class none of theirs
  const all = useMemo(() => (recs ?? []).filter((r) => classes === (!!r.class && r.role === "owner")), [recs, classes]);
  // Terminals or Agents, as the shares beside them are split (byj-1wv.45)
  const [picks, setSection] = useFilter(classes ? "class-recordings" : "recordings");
  const chipList: Chip[] = [
    { value: "all", label: t("classes:filterAll"), count: all.length },
    { value: "terminals", label: t("shares:terminalsTitle"), count: all.filter((r) => sectionOf(r.source) === "terminals").length },
    // recordings of agents' sessions, not shares: the Shares tab's own name is not theirs (byj-1wv.115.49)
    { value: "agents", label: t("shares:recordingsAgents"), count: all.filter((r) => sectionOf(r.source) === "agents").length },
  ];
  // a class's list has no chips: its classes are its headings
  const section = classes ? "all" : inForce(picks, chipList);
  const shown = (section === "all" ? all : all.filter((r) => sectionOf(r.source) === section))
    .slice()
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : String(err));
  const edit = async (r: Recording) => {
    const patch = await editRecording(r);
    if (patch === null) return;
    try {
      const updated = await update(r.id, patch);
      setRecs((rs) => (rs ?? []).map((x) => (x.id === r.id ? { ...x, ...updated, role: x.role } : x)));
    } catch (err) {
      fail(err);
    }
  };
  useEffect(() => {
    load().then(setRecs, fail);
  }, [load]);
  useEffect(() => {
    // to review: what others asked of me and I have not marked done
    if (recs !== null) onCount?.(all.length, all.filter((r) => r.role !== "owner" && !r.done_at).length);
  }, [recs, all, onCount]);
  const gone = (ids: string[]) => {
    setRecs((rs) => {
      const left = (rs ?? []).filter((x) => !ids.includes(x.id));
      mode.keepOnly(left.map((x) => x.id));
      return left;
    });
  };
  const del = async (r: Recording) => {
    // a class's instructor loses one made under an assignment: said first
    if (!(await confirm(phrases.deleteRecordingConfirm(r.title || r.id, instructorLoses(r)), { action: t("recordings:deleteRecording"), danger: true }))) return;
    try {
      await remove(r.id);
      gone([r.id]);
    } catch (err) {
      fail(err);
    }
  };
  // select all, delete and ask for reviews reach only what is in view
  const owned = shown.filter((r) => r.role === "owner").map((r) => r.id);
  const delPicked = async () => {
    const ids = owned.filter((id) => mode.picked.has(id));
    const loses = all.some((r) => ids.includes(r.id) && instructorLoses(r) !== undefined);
    if (ids.length === 0 || !(await confirm(phrases.deleteRecordingsConfirm(ids.length, loses), { action: t("recordings:deleteRecording"), danger: true }))) return;
    const done: string[] = [];
    for (const id of ids) {
      try {
        await remove(id);
        done.push(id);
      } catch (err) {
        fail(err);
      }
    }
    gone(done);
  };
  // several recordings, several people, one mail each (byj-1wv.44)
  const reviewPicked = async () => {
    const ids = owned.filter((id) => mode.picked.has(id));
    const req = ids.length > 0 ? await askReview(ids.length) : null;
    if (req === null) return;
    try {
      const { results } = await requestReviews(ids, req.emails, req.note);
      const sent = results.filter((x) => x.sent).map((x) => x.email);
      const failed = results.filter((x) => !x.sent).map((x) => x.email);
      setSentNote([sent.length ? t("recordings:reviewSent", { list: sent.join(", ") }) : "", failed.length ? t("recordings:reviewFailed", { list: failed.join(", ") }) : ""].join(" ").trim());
      mode.setAll(ids, false);
      // the rows say how far each asked has got (byj-xgt): read them again
      load().then(setRecs, fail);
    } catch (err) {
      fail(err);
    }
  };
  // the review column only when the relay said how a review stands for any row
  const review = reviewKnown(all);
  const select = (
    <SelectControls
      ids={owned}
      mode={mode}
      onDelete={() => void delPicked()}
      more={(n) => (
        <button className="small" onClick={() => void reviewPicked()}>
          {t("recordings:requestReview", { n })}
        </button>
      )}
    />
  );
  const groups: Group<Recording>[] = classes
    ? // each class under its name, newest class first, the way to it at its heading's end
      groupsBy(shown, (r) => ({ key: r.class!.id, title: r.class!.name })).map((g) => ({
        ...g,
        actions: (
          <Link to={`/c/${encodeURIComponent(g.key!)}`} className="button small">
            {t("classes:openClassPage")}
          </Link>
        ),
      }))
    : [
        // what others asked me to review and I have not marked done first:
        // it is what waits on me
        { title: t("shares:toReview"), items: shown.filter((r) => r.role !== "owner" && !r.done_at), className: "rows-head-review" },
        // only mine may be picked: Select stands over them
        { title: t("shares:mine"), items: shown.filter((r) => r.role === "owner"), actions: select },
        // what I have reviewed and marked done, last (byj-xgt)
        { title: t("shares:reviewedByMe"), items: shown.filter((r) => r.role !== "owner" && !!r.done_at) },
      ];

  return (
    <>
      {confirmDialog}
      {editDialog}
      {reviewDialog}
      {sentNote && (
        <p className="muted small" role="status">
          {sentNote}
        </p>
      )}
      {error ? (
        <p className="error">{t("common:error", { m: error })}</p>
      ) : recs === null ? (
        <p className="muted">{t("common:loading")}</p>
      ) : all.length === 0 ? (
        <p className="muted">{classes ? t("recordings:noClassRecordingsMine") : t("recordings:noRecordings")}</p>
      ) : (
        <Grouped
          id={classes ? "class-recordings" : "recordings"}
          headed={classes}
          tools={
            classes ? (
              // the classes are the headings; Select over them all, at the line's end
              <>
                <span className="grow" />
                {select}
              </>
            ) : (
              <FilterChips label={t("shares:filterBy", { what: t("recordings:recordingsTitle") })} value={section} onChange={setSection} chips={chipList} />
            )
          }
          groups={groups}
          row={(r) => (
            <RecordingRow
              key={r.id}
              r={r}
              review={review}
              select={mode.selecting && r.role === "owner" ? { picked: mode.picked, toggle: mode.toggle } : undefined}
              menu={
                r.role === "owner"
                  ? [
                      { label: t("common:edit"), onSelect: () => void edit(r) },
                      { label: t("recordings:deleteRecording"), onSelect: () => void del(r), danger: true },
                    ]
                  : []
              }
            />
          )}
        />
      )}
    </>
  );
}
